import { escapeHtml as esc, openPrintWindow } from '@/components/documents';
import { formatDate, formatMoney, formatNumber } from '@/format';
import { amountInWords, periodLabel, PAY_METHOD_TEXT, type PaymentMethod } from '@/hooks/hr';
import { company } from '@/stores/company';

/**
 * Print layouts for HR: payslips, payment sheets, and any table (the payroll
 * register, reports, a tax certificate). All go through openPrintWindow, so
 * they carry the same letterhead and footer as every other printout.
 */

export interface PrintablePayslip {
  documentNo: string;
  runTitle?: string | null;
  periodStart: string | null;
  periodEnd: string | null;
  employeeCode: string;
  employeeName: string;
  employeeNameUr?: string | null;
  designationName?: string | null;
  departmentName?: string | null;
  farm: string;
  cnic?: string | null;
  paymentMethod: PaymentMethod;
  payIban?: string | null;
  walletNo?: string | null;
  payableDays: string;
  absentDays?: string;
  paidLeaveDays?: string;
  overtimeHours?: string;
  grossSalary?: string | null;
  totalDeductions?: string | null;
  netPayable?: string | null;
  employerCost?: string | null;
  lines: Array<{ label: string; kind: string; quantity: string | null; payAmount?: string | null }>;
  advancesOwed?: Array<{ documentNo: string; kind: string; outstandingAmount?: string | null }>;
  remarks?: string | null;
  paidOn?: string | null;
}

const PAYSLIP_CSS = `
  .slip { border: 1px solid #999; padding: 0.8rem 1rem; margin-block-end: 1rem; }
  .slip h2 { display: flex; justify-content: space-between; margin: 0 0 0.6rem; font-size: 1.05rem; }
  .slip .who { display: grid; grid-template-columns: repeat(2, 1fr); gap: 0.15rem 1.5rem; font-size: 0.85rem; margin-block-end: 0.6rem; }
  .slip .who b { font-weight: 600; }
  .slip .cols { display: grid; grid-template-columns: 1fr 1fr; gap: 0.8rem; }
  .slip table th { background: #f3f3f3; }
  .slip td, .slip th { font-size: 0.82rem; padding: 0.25rem 0.45rem; }
  .slip .net { margin-block-start: 0.6rem; padding: 0.5rem 0.7rem; border: 2px solid #111; display: flex; justify-content: space-between; font-size: 1rem; font-weight: 700; }
  .slip .words { font-size: 0.82rem; margin-block-start: 0.25rem; font-style: italic; }
  .slip .foot { display: flex; justify-content: space-between; margin-block-start: 1.6rem; font-size: 0.8rem; }
  .slip .sign { border-top: 1px solid #111; padding-top: 0.2rem; min-width: 12rem; text-align: center; }
  .slip .small { font-size: 0.78rem; color: #333; margin-block-start: 0.4rem; }
`;

function linesTable(title: string, titleUr: string, rows: PrintablePayslip['lines'], total: string | null | undefined) {
  const body = rows.length
    ? rows
        .map(
          (l) =>
            `<tr><td>${esc(l.label)}${l.quantity ? ` <span style="color:#555">(${esc(formatNumber(l.quantity))})</span>` : ''}</td><td class="num">${esc(formatMoney(l.payAmount))}</td></tr>`,
        )
        .join('')
    : '<tr><td colspan="2" style="color:#555">None</td></tr>';
  return `<table><thead><tr><th>${title} <span class="urdu" lang="ur">${titleUr}</span></th><th class="num">Rs</th></tr></thead>
    <tbody>${body}</tbody>
    <tfoot><tr><th>Total</th><th class="num">${esc(formatMoney(total))}</th></tr></tfoot></table>`;
}

function slipHtml(s: PrintablePayslip): string {
  const c = company();
  const earnings = s.lines.filter((l) => l.kind === 'EARNING');
  const deductions = s.lines.filter((l) => l.kind === 'DEDUCTION');
  const period = s.periodStart && s.periodEnd ? periodLabel(s.periodStart, s.periodEnd) : '';
  const account =
    s.paymentMethod === 'BANK' && s.payIban
      ? ` · ${esc(s.payIban)}`
      : s.paymentMethod === 'WALLET' && s.walletNo
        ? ` · ${esc(s.walletNo)}`
        : '';
  const owed = (s.advancesOwed ?? []).filter((a) => a.outstandingAmount);
  return `<section class="slip">
    <h2><span>${esc(c.nameEn)} — Payslip <span class="urdu" lang="ur">تنخواہ کی پرچی</span></span><span>${esc(period)}</span></h2>
    <div class="who">
      <div><b>Name:</b> ${esc(s.employeeName)}${s.employeeNameUr ? ` <span class="urdu" lang="ur">${esc(s.employeeNameUr)}</span>` : ''}</div>
      <div><b>Code:</b> ${esc(s.employeeCode)}</div>
      <div><b>Designation:</b> ${esc(s.designationName ?? '—')}</div>
      <div><b>Farm:</b> ${esc(s.farm)}</div>
      ${s.cnic ? `<div><b>CNIC:</b> ${esc(s.cnic)}</div>` : ''}
      <div><b>Pay run:</b> ${esc(s.documentNo)}${s.runTitle ? ` — ${esc(s.runTitle)}` : ''}</div>
      <div><b>Days paid:</b> ${esc(formatNumber(s.payableDays))}${
        s.absentDays && Number(s.absentDays) > 0 ? ` · absent ${esc(formatNumber(s.absentDays))}` : ''
      }${s.overtimeHours && Number(s.overtimeHours) > 0 ? ` · overtime ${esc(formatNumber(s.overtimeHours))} h` : ''}</div>
      <div><b>Paid by:</b> ${esc(PAY_METHOD_TEXT[s.paymentMethod])}${account}</div>
    </div>
    <div class="cols">
      ${linesTable('Earnings', 'آمدنی', earnings, s.grossSalary)}
      ${linesTable('Deductions', 'کٹوتیاں', deductions, s.totalDeductions)}
    </div>
    <div class="net"><span>Net pay <span class="urdu" lang="ur">خالص تنخواہ</span></span><span>${esc(formatMoney(s.netPayable))}</span></div>
    ${s.netPayable ? `<div class="words">${esc(amountInWords(s.netPayable))}</div>` : ''}
    ${owed.length ? `<div class="small">Still owed: ${owed.map((a) => `${esc(a.documentNo)} ${esc(formatMoney(a.outstandingAmount))}`).join(' · ')}</div>` : ''}
    ${s.remarks ? `<div class="small">Remarks: ${esc(s.remarks)}</div>` : ''}
    ${s.paidOn ? `<div class="small">Paid on ${esc(formatDate(s.paidOn))}</div>` : ''}
    <div class="foot"><span class="sign">Prepared by</span><span class="sign">Received by (signature / thumb) <span class="urdu" lang="ur">وصول کنندہ</span></span></div>
  </section>`;
}

/** One payslip per page (A5), or several to an A4 page for a farm's batch. */
export function printPayslips(slips: PrintablePayslip[], page: 'A4' | 'A5' = 'A5'): void {
  if (slips.length === 0) return;
  const body = slips
    .map((s, i) => `${slipHtml(s)}${page === 'A5' && i < slips.length - 1 ? '<div class="pagebreak"></div>' : ''}`)
    .join('');
  openPrintWindow({
    title: slips.length === 1 ? `Payslip ${slips[0]!.employeeCode}` : `Payslips (${slips.length})`,
    body,
    css: PAYSLIP_CSS,
    page,
  });
}

export interface SheetRow {
  employeeCode: string;
  employeeName: string;
  employeeNameUr?: string | null;
  designationName?: string | null;
  farm: string;
  cnic?: string | null;
  payBankName?: string | null;
  payAccountTitle?: string | null;
  payIban?: string | null;
  walletProvider?: string | null;
  walletNo?: string | null;
  netPayable?: string | null;
}

/**
 * The payment sheet: for cash, one table per farm with a column for the
 * person's signature or thumbprint as they are paid; for bank and wallet,
 * the account each amount goes to.
 */
export function printPaySheet(sheet: {
  documentNo: string;
  periodStart: string | null;
  periodEnd: string | null;
  method: PaymentMethod;
  items: SheetRow[];
}): void {
  const byFarm = new Map<string, SheetRow[]>();
  for (const r of sheet.items) byFarm.set(r.farm, [...(byFarm.get(r.farm) ?? []), r]);
  const cash = sheet.method === 'CASH';
  const period = sheet.periodStart && sheet.periodEnd ? periodLabel(sheet.periodStart, sheet.periodEnd) : '';
  const sum = (rows: SheetRow[]) => rows.reduce((t, r) => t + Number(r.netPayable ?? 0), 0);

  const groups = [...byFarm.entries()]
    .map(([farm, rows], gi) => {
      const head = cash
        ? '<th>#</th><th>Code</th><th>Name</th><th>Designation</th><th class="num">Net pay</th><th style="width:9rem">Signature / thumb</th>'
        : sheet.method === 'BANK'
          ? '<th>#</th><th>Code</th><th>Name</th><th>CNIC</th><th>Bank</th><th>Account title</th><th>IBAN</th><th class="num">Amount</th>'
          : '<th>#</th><th>Code</th><th>Name</th><th>CNIC</th><th>Wallet</th><th>Number</th><th class="num">Amount</th>';
      const body = rows
        .map((r, i) => {
          const name = `${esc(r.employeeName)}${r.employeeNameUr ? ` <span class="urdu" lang="ur">${esc(r.employeeNameUr)}</span>` : ''}`;
          const amount = `<td class="num">${esc(formatMoney(r.netPayable))}</td>`;
          if (cash) {
            return `<tr><td>${i + 1}</td><td>${esc(r.employeeCode)}</td><td>${name}</td><td>${esc(r.designationName ?? '')}</td>${amount}<td style="height:2.2rem"></td></tr>`;
          }
          if (sheet.method === 'BANK') {
            return `<tr><td>${i + 1}</td><td>${esc(r.employeeCode)}</td><td>${name}</td><td>${esc(r.cnic ?? '')}</td><td>${esc(r.payBankName ?? '')}</td><td>${esc(r.payAccountTitle ?? '')}</td><td>${esc(r.payIban ?? '—')}</td>${amount}</tr>`;
          }
          return `<tr><td>${i + 1}</td><td>${esc(r.employeeCode)}</td><td>${name}</td><td>${esc(r.cnic ?? '')}</td><td>${esc(r.walletProvider ?? '')}</td><td>${esc(r.walletNo ?? '—')}</td>${amount}</tr>`;
        })
        .join('');
      const span = cash ? 4 : sheet.method === 'BANK' ? 7 : 6;
      return `<h2>${esc(farm)}</h2><table><thead><tr>${head}</tr></thead><tbody>${body}</tbody>
        <tfoot><tr><th colspan="${span}">Total for ${esc(farm)} (${rows.length})</th><th class="num">${esc(formatMoney(sum(rows)))}</th>${cash ? '<th></th>' : ''}</tr></tfoot></table>
        ${cash ? '<p style="margin-top:1.2rem;font-size:0.85rem">Paid by: ____________________ &nbsp;&nbsp; Checked by: ____________________</p>' : ''}
        ${cash && gi < byFarm.size - 1 ? '<div class="pagebreak"></div>' : ''}`;
    })
    .join('');

  openPrintWindow({
    title: `${PAY_METHOD_TEXT[sheet.method]} sheet — ${sheet.documentNo}`,
    body: `<h1>${esc(PAY_METHOD_TEXT[sheet.method])} sheet — ${esc(sheet.documentNo)} · ${esc(period)}</h1>
      <p>${sheet.items.length} people · total ${esc(formatMoney(sum(sheet.items)))}${
        sum(sheet.items) > 0 ? ` (${esc(amountInWords(sum(sheet.items)))})` : ''
      }</p>${groups}`,
    page: sheet.method === 'CASH' ? 'A4' : 'A4 landscape',
  });
}

export interface PrintColumn {
  key: string;
  header: string;
  kind?: 'text' | 'money' | 'number' | 'date' | undefined;
}

function cell(value: unknown, kind: PrintColumn['kind']): string {
  if (value === null || value === undefined || value === '') return '';
  if (kind === 'money') return formatMoney(String(value));
  if (kind === 'number') return formatNumber(String(value));
  if (kind === 'date') return formatDate(String(value));
  return String(value);
}

/** Any table — the payroll register, a report, a tax certificate. Columns
 *  whose values the server withheld (masked) are left out, not blank. */
export function printTable(doc: {
  title: string;
  subtitle?: string | null | undefined;
  columns: PrintColumn[];
  rows: Array<Record<string, unknown>>;
  landscape?: boolean;
  signatures?: string[];
}): void {
  const cols = doc.columns.filter((c) => doc.rows.length === 0 || doc.rows.some((r) => c.key in r));
  const numeric = (c: PrintColumn) => c.kind === 'money' || c.kind === 'number';
  const head = cols.map((c) => `<th${numeric(c) ? ' class="num"' : ''}>${esc(c.header)}</th>`).join('');
  const body = doc.rows
    .map(
      (r) =>
        `<tr>${cols.map((c) => `<td${numeric(c) ? ' class="num"' : ''}>${esc(cell(r[c.key], c.kind))}</td>`).join('')}</tr>`,
    )
    .join('');
  const signatures = doc.signatures?.length
    ? `<div style="display:flex;gap:3rem;margin-top:2.5rem">${doc.signatures
        .map((s) => `<span style="border-top:1px solid #111;padding-top:0.2rem;min-width:11rem;text-align:center;font-size:0.8rem">${esc(s)}</span>`)
        .join('')}</div>`
    : '';
  openPrintWindow({
    title: doc.title,
    body: `<h1>${esc(doc.title)}</h1>${doc.subtitle ? `<p>${esc(doc.subtitle)}</p>` : ''}
      <table><thead><tr>${head}</tr></thead><tbody>${body || `<tr><td colspan="${cols.length}">Nothing to show</td></tr>`}</tbody></table>${signatures}`,
    css: 'th { background: #f3f3f3; } td, th { font-size: 0.8rem; }',
    page: doc.landscape ? 'A4 landscape' : 'A4',
  });
}
